import React, { useEffect, useCallback, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Nav from "./Nav";
import Footer from "./Footer";
import Cursor from "./Cursor";
import Preloader from "./Preloader";

const EASE = [0.76, 0, 0.24, 1];

export default function Layout({ children, location }) {
  const [ready, setReady] = useState(false);
  const onDone = useCallback(() => setReady(true), []);

  useEffect(() => {
    let lenis, raf;
    (async () => {
      const { default: Lenis } = await import("lenis");
      lenis = new Lenis({ lerp: 0.085, wheelMultiplier: 1, smoothWheel: true });
      window.__lenis = lenis;
      const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
      raf = requestAnimationFrame(loop);
    })();
    return () => { cancelAnimationFrame(raf); lenis?.destroy(); window.__lenis = null; };
  }, []);

  useEffect(() => {
    // New page: jump to top after the curtain covers the screen.
    const t = setTimeout(() => { window.scrollTo(0, 0); window.__lenis?.scrollTo(0, { immediate: true }); }, 350);
    return () => clearTimeout(t);
  }, [location.pathname]);

  return (
    <>
      <Preloader onDone={onDone} />
      <Cursor />
      <div className="grain" aria-hidden="true" />
      <Nav path={location.pathname} />
      <AnimatePresence mode="wait">
        <motion.main
          key={location.pathname}
          initial={{ opacity: 0 }}
          animate={{ opacity: ready ? 1 : 0 }}
          exit={{ opacity: 0, transition: { duration: 0.35 } }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.35 }}
        >
          {children}
          <Footer />
        </motion.main>
      </AnimatePresence>
      <AnimatePresence>
        <motion.div
          key={`curtain-${location.pathname}`}
          className="curtain"
          initial={{ scaleY: 1 }}
          animate={{ scaleY: 0, transition: { duration: 0.9, ease: EASE, delay: 0.25 } }}
          exit={{ scaleY: 1, transformOrigin: "bottom", transition: { duration: 0.5, ease: EASE } }}
          style={{ transformOrigin: "top" }}
        />
      </AnimatePresence>
      <style dangerouslySetInnerHTML={{ __html: `.curtain { position: fixed; inset: 0; z-index: 2000; background: var(--gold); pointer-events: none; }` }} />
    </>
  );
}
